import { useState, useEffect } from "react";
import Logout from "../components/Logout";
import NavigationButton from "./NavigationButton";
import { getTagDefinitions, getPurchasesBySchool, whoami } from "../services/APIClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

import { withRouter } from "../utils/withRouter.js";
import { buildCsdPortalUrl } from "../utils/csdWorkflowEmbed";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import "./HeadingBanner.scss";
import useAcademicYearOptions from "../utils/useAcademicYearOptions";

function HeadingBanner(props) {
  const colour = useState("grey");
  let pilotData = props.pilotData;
  let screeningWindowData = props.screeningWindowData;

  // Some pages don't pass schoolid down as a prop (e.g. Results, BeforeScreening),
  // which broke the Help modal there since it needs schoolid to build its URL.
  // Fall back to resolving it ourselves so Help works regardless of the page.
  const [resolvedSchoolid, setResolvedSchoolid] = useState(props.schoolid);
  const schoolid = props.schoolid || resolvedSchoolid;

  useEffect(() => {
    if (props.schoolid) return;

    whoami()
      .then((response) => {
        setResolvedSchoolid(response.data.stakeholder.schoolid);
      })
      .catch(() => {});
  }, [props.schoolid]);

  // Fetch latest purchase data internally
  const [latestPurchase, setLatestPurchase] = useState(null);

  useEffect(() => {
    const fetchLatestPurchase = async () => {
      if (!schoolid) return;

      try {
        const purchaseResponse = await getPurchasesBySchool(schoolid, {
          where: { next_payment_date: { neq: null } },
          order: "next_payment_date DESC",
          limit: 1,
        });

        setLatestPurchase(
          Array.isArray(purchaseResponse) && purchaseResponse.length > 0
            ? purchaseResponse[0]
            : null
        );
      } catch (error) {
        console.error("Error fetching purchase data:", error);
        setLatestPurchase(null);
      }
    };

    fetchLatestPurchase();
  }, [schoolid]);

  const nextPaymentDate = latestPurchase?.next_payment_date;
  const isPilot = pilotData && pilotData.length > 0 && pilotData[0].active;
  const pilotAcademicYear = isPilot ? pilotData[0].academic_year : null;

  const [showHelp, setShowHelp] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  useEffect(() =>
  {
    if (!props.isLoggedIn) return;

    getTagDefinitions()
      .then((tags) =>
      {
        const tag = tags.find(t => t.tag_name === 'show_help_enabled');
        if (tag)
        {
          setShowHelp(tag.default_value === 'ON');
        }
      })
      .catch(() => {});
  }, [props.isLoggedIn]);

  const openHelpCentre = () => {
    window.localStorage.setItem("td_return_url", window.location.href);
    setShowHelpModal(true);
  };

  const helpEmbedUrl = showHelpModal ? buildCsdPortalUrl(schoolid) : "";

  const helpButton = showHelp && props.isLoggedIn && (
    <div className="helpPosition">
      <button
        type="button"
        className="help-button-prominent"
        onClick={openHelpCentre}
        aria-label="Help Centre"
      >
        <FontAwesomeIcon icon={faQuestionCircle} className="help-button-icon" />
        <span className="help-button-label">Help Centre</span>
      </button>
    </div>
  );

  const cloudContent = (
    <div>
      <div className="leftPosition">
        <NavigationButton
          img="/assets/imgs/home_icon.svg"
          alt="home"
          route="/masterdashboard"
          isLoggedIn={props.isLoggedIn}
          teacherid={props.teacherid}
          className="navigation-icon"
        />
      </div>
      <div className="centrePosition">
        <NavigationButton
          img="/assets/imgs/avatar.svg"
          alt="profile"
          route="/profile"
          isLoggedIn={props.isLoggedIn}
          teacherid={props.teacherid}
          className="navigation-icon"
        />
      </div>
      <div className="rightPosition">
        <Logout isLoggedIn={props.isLoggedIn} />
      </div>
      {showHelpModal && (
        <div
          className="help-modal-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowHelpModal(false);
            }
          }}
        >
          <div
            className="help-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="help-modal-title"
          >
            <div className="help-modal-header">
              <h2 id="help-modal-title">Help Centre</h2>
              <button
                type="button"
                className="help-modal-close"
                onClick={() => setShowHelpModal(false)}
                aria-label="Close help"
              >
                Close
              </button>
            </div>
            <div className="help-modal-body">
              {helpEmbedUrl ? (
                <iframe
                  title="Help Centre"
                  className="help-modal-frame"
                  src={helpEmbedUrl}
                />
              ) : (
                <p className="help-modal-error">
                  Help is unavailable right now. Please try again later.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="headerContainer">
      <MainBanner
        name={props.name}
        cloud={props.cloud}
        control={cloudContent}
        help={helpButton}
        nextPaymentDate={nextPaymentDate}
        isPilot={isPilot}
        pilotAcademicYear={pilotAcademicYear}
        screeningWindowData={screeningWindowData}
        schoolid={schoolid}
        isLoggedIn={props.isLoggedIn}
      />
    </div>
  );
}

function MainBanner(props) {
  const [currentAcademicYear] = useAcademicYearOptions({ previousYears: 0 });
  let name = props.name;
  let cloud = props.cloud;
  let control = props.control;
  let nextPaymentDate = props.nextPaymentDate;
  let isPilot = props.isPilot;
  let pilotAcademicYear = props.pilotAcademicYear;
  let screeningWindowData = props.screeningWindowData;
  let schoolid = props.schoolid;
  let isLoggedIn = props.isLoggedIn;
  let help = props.help;

  // Only show purchase info if logged in
  let combinedInfo = "";
  
  if (isLoggedIn) {
    const now = new Date();
    
    const currentSchoolYear = currentAcademicYear.display;
    
    const parsedDate = nextPaymentDate ? new Date(nextPaymentDate) : null;
    const isValidDate = parsedDate && !Number.isNaN(parsedDate.getTime());

    let displayPurchasedText = null;
    const hasSchoolContext = schoolid || (isPilot && pilotAcademicYear) || (screeningWindowData && screeningWindowData.length > 0) || isValidDate;

    if (!isValidDate) {
      // next_payment_date is null so no purchased record
      if (hasSchoolContext) {
        // if we have pilot info, show pilot; otherwise show Not Purchased for the current school year
        if (isPilot && pilotAcademicYear) {
          displayPurchasedText = `Pilot (${pilotAcademicYear})`;
        } else {
          displayPurchasedText = `Not Purchased (${currentSchoolYear})`;
        }
      }
    } else {
      // Derive school year from next_payment_date
      // end year = year of next_payment_date, start year = end year - 1
      const endYear = parsedDate.getFullYear();
      const startYear = endYear - 1;
      const startShort = String(startYear).slice(-2);
      const endShort = String(endYear).slice(-2);
      const schoolYear = `${startShort}/${endShort}`;

      if (now <= parsedDate) {
        // Active purchase
        displayPurchasedText = `Purchased (${schoolYear})`;
      } else {
        // Lapsed but check if pilot is active to override lapsed status
        if (isPilot && pilotAcademicYear) {
          displayPurchasedText = `Pilot (${pilotAcademicYear})`;
        } else {
          displayPurchasedText = `Not Purchased (${schoolYear})`;
        }
      }
    }

    // Get dynamic screening message from API
    const screeningMessage = screeningWindowData && screeningWindowData.length > 0 
      ? screeningWindowData[0].message 
      : null;

    // Show screening message when we have a valid date or pilot
    const showScreeningMessage = isValidDate || isPilot;

    // Combine texts into the same span
    const infoPieces = [];
    if (displayPurchasedText) infoPieces.push(displayPurchasedText);
    if (showScreeningMessage && screeningMessage) infoPieces.push(screeningMessage);
    combinedInfo = infoPieces.length ? ` (${infoPieces.join(". ")})` : "";
  }

  return (
    <Row className="subheader">
      <Col className="d-flex">
        <div className="logo col-2"></div>
        <h2 className="subheading-text col-7">
          {name}
          {combinedInfo && (
            <span className="purchased-text">{combinedInfo}</span>
          )}
        </h2>
        <div className={`${cloud ? "cloud" : "circle"}`}>{control}</div>
        {help}
      </Col>
    </Row>
  );
}

export default withRouter(HeadingBanner);
